import classNames from "classnames";
import React from "react";
import { c, t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { Link } from "../../../common/Link";
import { Popover } from "../../../common/Popover";
import { RatingGraphic } from "../../../common/RatingGraphic";
import { IProduct } from "../../../models/catalogue";
import { IWebPageURL, isoFormUUID } from "../../../models/nominals";
import { IHistogramEntry } from "../../../models/reviews";
import { formatNumber } from "../../../utils/format";
import { roundReviewRating } from "../../../utils/math";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { WriteReviewFormMode } from "../../reviews/constants";
import { setWriteReviewFormMode } from "../../reviews/reducers";
import { loadReviewTemplate } from "../../reviews/thunks";
import { setRatingsModalState } from "../reducers";
import { reviewHistogramSelector, rootProductSelector } from "../selectors";
import { RatingsModalRow } from "./RatingsModalRow";

import styles from "./RatingsSummary.module.scss";

const scrollToReviewsModule = (dispatch: ReturnType<typeof useAppDispatch>) => {
    dispatch(setRatingsModalState({ isOpen: false }));
    // Scroll and focus on the next tick since closing the modal will change focus also. We
    // need to make sure this happens after that.
    setTimeout(() => {
        const document = getDocument();
        const reviewsBody = document?.querySelector<HTMLElement>(
            ".customer-review__body",
        );
        reviewsBody?.scrollIntoView(true);
        reviewsBody?.focus();
    }, 0);
};

const handleWriteReview = async (
    selectedRootProduct: IProduct | null,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    dispatch(setRatingsModalState({ isOpen: false }));
    if (!selectedRootProduct || !selectedRootProduct.reviews_product_id) {
        return;
    }
    const document = getDocument();
    const writeReviewForm =
        document?.querySelector<HTMLElement>(".write-review-form");
    const writeReviewFormUUIDstr = writeReviewForm?.dataset.formUuid;
    if (!writeReviewForm || !writeReviewFormUUIDstr) {
        return;
    }
    // Load review template (handles API call and stores template in state)
    const writeReviewFormUUID = isoFormUUID.wrap(writeReviewFormUUIDstr);
    await dispatch(
        loadReviewTemplate({
            formUUID: writeReviewFormUUID,
            productID: selectedRootProduct.reviews_product_id,
        }),
    );
    // Open form
    dispatch(
        setWriteReviewFormMode({
            formUUID: writeReviewFormUUID,
            mode: WriteReviewFormMode.FORM_OPEN,
        }),
    );
    // Scroll to form
    writeReviewForm.scrollIntoView(true);
};

const buildPopoverContent = (
    histogram: ReadonlyArray<IHistogramEntry>,
    avgRating: number,
    totalReviewCount: number,
    onWriteReview: () => void,
    onViewAllReviews: () => void,
) => {
    const formattedCount = formatNumber(totalReviewCount);
    // Render empty state?
    if (totalReviewCount <= 0) {
        return (
            <div
                className={`${styles.modalContent} configurator-rating-filter`}
            >
                <button
                    className="configurator-rating-filter__button configurator-rating-filter__empty-rating"
                    onClick={onWriteReview}
                >
                    {t`Be the first to write a review`}
                </button>
            </div>
        );
    }
    // Render rating distribution rows
    return (
        <div className={`${styles.modalContent} configurator-rating-filter`}>
            <h2 className={styles.title}>
                {c("ratings-summary").t`${avgRating} out of 5 stars`}
            </h2>
            {histogram.map((entry) => (
                <RatingsModalRow
                    key={entry.count}
                    totalReviewCount={totalReviewCount}
                    entry={entry}
                    onClickRating={onViewAllReviews}
                />
            ))}
            <button className={styles.button} onClick={onViewAllReviews}>
                {/* TODO(i18n): Missing plural handling; use ngettext: ngettext(msgid`View ${formattedCount} review`, `View all ${formattedCount} reviews`, totalReviewCount) */}
                {t`View all ${formattedCount} reviews`}
            </button>
        </div>
    );
};

const buildStaticSummary = (
    avgRating: number,
    totalReviewCount: number,
    starRatingURL?: IWebPageURL,
) => {
    if (!starRatingURL || totalReviewCount <= 0) {
        return null;
    }
    return (
        <Link href={starRatingURL} title={t`Read Reviews`}>
            <div className="configurator__star-rating">
                <RatingGraphic
                    cardClass="configurator"
                    cardSize="large"
                    numReviews={totalReviewCount}
                    rating={avgRating}
                />
            </div>
        </Link>
    );
};

const buildPopoverSummary = (
    histogram: ReadonlyArray<IHistogramEntry>,
    avgRating: number,
    totalReviewCount: number,
    modalIsOpen: boolean,
    onRequestOpen: () => void,
    onRequestClose: () => void,
    onViewAllReviews: () => void,
    popoverContent: React.ReactNode,
) => {
    const formattedCount = formatNumber(totalReviewCount);
    return (
        <>
            <Popover
                isOpen={modalIsOpen}
                onRequestOpen={onRequestOpen}
                onRequestClose={onRequestClose}
                popperOptions={{
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 16],
                            },
                        },
                    ],
                    placement: "bottom",
                }}
                modalProps={{
                    className: `${styles.modal} configurator-rating-filter-container__modal`,
                    contentLabel: t`Filter Star Ratings`,
                }}
                triggerContent={
                    <div className="configurator__star-rating">
                        <RatingGraphic
                            cardClass="configurator"
                            cardSize="large"
                            onlyStars={true}
                            rating={avgRating}
                            starHasStroke={false}
                        />
                    </div>
                }
            >
                {popoverContent}
            </Popover>
            <button
                aria-describedby="configurator-rating-filter"
                className={styles.reviewsNumber}
                onClick={onViewAllReviews}
            >
                {/* TODO(i18n): Missing plural handling; use ngettext: ngettext(msgid`${formattedCount} Review`, `${formattedCount} Reviews`, totalReviewCount) */}
                {t`${formattedCount} Reviews`}
            </button>
        </>
    );
};

export const RatingsSummary = (props: {
    isPanel?: boolean;
    starRatingURL?: IWebPageURL;
    showModal?: boolean;
}): React.ReactNode => {
    const selectedRootProduct = useAppSelector((rootState) =>
        rootProductSelector(rootState.configurator),
    );
    const modalIsOpen = useAppSelector(
        (rootState) => rootState.configurator.ui.ratingsModalOpen,
    );
    const histogram = useAppSelector((rootState) =>
        reviewHistogramSelector(
            rootState.reviews.data.products,
            selectedRootProduct?.uuid ?? null,
        ),
    );

    const dispatch = useAppDispatch();

    const onWriteReview = async () => {
        await handleWriteReview(selectedRootProduct, dispatch);
    };

    const onViewAllReviews = () => {
        scrollToReviewsModule(dispatch);
    };

    const onRequestClose = () => {
        dispatch(setRatingsModalState({ isOpen: false }));
    };

    const onRequestOpen = () => {
        dispatch(setRatingsModalState({ isOpen: true }));
    };

    const totalReviewCount = histogram.reduce(
        (memo, entry) => memo + entry.count,
        0,
    );
    const combinedScore = histogram.reduce(
        (memo, entry) => memo + entry.count * entry.rating,
        0,
    );
    const avgRating = roundReviewRating(combinedScore / totalReviewCount);

    const popoverContent = buildPopoverContent(
        histogram,
        avgRating,
        totalReviewCount,
        onWriteReview,
        onViewAllReviews,
    );

    const containerClasses = classNames({
        [styles.container]: true,
        [styles.centerContent]: props.isPanel,
        "configurator-rating-filter-container": true,
    });

    return (
        <div className={containerClasses}>
            {props.showModal && totalReviewCount > 0
                ? buildPopoverSummary(
                      histogram,
                      avgRating,
                      totalReviewCount,
                      modalIsOpen,
                      onRequestOpen,
                      onRequestClose,
                      onViewAllReviews,
                      popoverContent,
                  )
                : buildStaticSummary(
                      avgRating,
                      totalReviewCount,
                      props.starRatingURL,
                  )}
        </div>
    );
};
